import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.new_password_confirm) return toast.error("Yeni şifreler eşleşmiyor");
    if (pwd.new_password.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    setPwdLoading(true);
    try {
      await api.post("/admin/change-password", pwd);
      toast.success("Şifre başarıyla değiştirildi");
      setPwd({ current_password: "", new_password: "", new_password_confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwdLoading(false);
    }
  };
  const [form, setForm] = useState({
    grizzly_api_key: "",
    grizzly_enabled: "1",
    smsactivate_api_key: "",
    smsactivate_enabled: "0",
    fivesim_api_key: "",
    fivesim_enabled: "0",
    tigersms_api_key: "",
    tigersms_enabled: "0",
    herosms_api_key: "",
    herosms_enabled: "0",
    iban_name: "",
    iban_number: "",
    iban_bank: "",
    crypto_btc: "",
    crypto_usdt_trc20: "",
    crypto_eth: "",
    profit_multiplier: "1.8",
    usd_to_try: "34.0",
    tr_wa_standard_price: "79.38",
    tr_wa_premium_price: "99.90",
    tr_wa_premium_plus_price: "160.00",
    shopier_api_key: "",
    shopier_api_secret: "",
    shopier_enabled: "0",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setForm({ ...form, [k]: form[k] === "1" ? "0" : "1" });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/settings", {
        ...form,
        profit_multiplier: parseFloat(form.profit_multiplier) || 1,
        usd_to_try: parseFloat(form.usd_to_try) || 34,
      });
      toast.success("Ayarlar kaydedildi");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, k, mono, placeholder }) => (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">{label}</label>
      <input
        data-testid={`settings-${k}`}
        value={form[k] || ""}
        onChange={change(k)}
        placeholder={placeholder}
        className={`w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(43,74%,55%)] outline-none px-3 py-2.5 text-sm rounded-sm ${mono ? "font-mono" : ""}`}
      />
    </div>
  );

  const Toggle = ({ k, label }) => {
    const on = form[k] === "1";
    return (
      <button type="button" onClick={toggle(k)} data-testid={`toggle-${k}`}
        className={`inline-flex items-center gap-2 border rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
          on ? "border-[hsl(142,71%,55%)] text-[hsl(142,71%,55%)] bg-[hsl(142,71%,55%)]/10" : "border-white/20 text-white/50"
        }`}>
        <span className={`w-2 h-2 rounded-full ${on ? "bg-[hsl(142,71%,55%)]" : "bg-white/30"}`} />
        {label}: {on ? "Aktif" : "Pasif"}
      </button>
    );
  };

  const ProviderCard = ({ title, subtitle, keyField, enabledField, href, badge }) => (
    <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-display font-bold text-lg">{title}</div>
            {badge && <span className="text-[9px] uppercase tracking-wider bg-[hsl(43,74%,45%)]/20 text-[hsl(43,74%,65%)] border border-[hsl(43,74%,55%)]/40 px-1.5 py-0.5 rounded">{badge}</span>}
          </div>
          <div className="text-xs text-white/50 mt-1">{subtitle}</div>
        </div>
        <Toggle k={enabledField} label={title.split(" ")[0]} />
      </div>
      <Field label="API Key" k={keyField} mono placeholder="API anahtarınızı yapıştırın" />
      <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block text-xs text-[hsl(43,74%,65%)] hover:text-white transition-colors">
        API key almak için → {href.replace("https://", "")}
      </a>
    </div>
  );

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Konfigürasyon</div>
        <h1 className="font-display text-3xl font-black">Ayarlar</h1>
      </div>

      <form onSubmit={save} className="space-y-6 max-w-3xl">
        <div>
          <h2 className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold mb-3">SMS Sağlayıcıları</h2>
          <div className="space-y-3">
            <ProviderCard
              title="Grizzly SMS"
              subtitle="Ana sağlayıcı — Türkiye stoku yüksek"
              keyField="grizzly_api_key"
              enabledField="grizzly_enabled"
              href="https://grizzlysms.com/en/api"
              badge="Ana"
            />
            <ProviderCard
              title="SMS-Activate"
              subtitle="En büyük havuz — Grizzly ile aynı API yapısı"
              keyField="smsactivate_api_key"
              enabledField="smsactivate_enabled"
              href="https://sms-activate.io/en/api2"
            />
            <ProviderCard
              title="5sim"
              subtitle="Modern REST API — farklı sağlayıcı ağı, ek stok"
              keyField="fivesim_api_key"
              enabledField="fivesim_enabled"
              href="https://5sim.net/docs"
            />
            <ProviderCard
              title="Tiger SMS"
              subtitle="Ucuz TR stoğu — SMS-Activate uyumlu API"
              keyField="tigersms_api_key"
              enabledField="tigersms_enabled"
              href="https://tiger-sms.com/en/api"
            />
            <ProviderCard
              title="Hero SMS"
              subtitle="Güçlü TR stoğu — SMS-Activate uyumlu API"
              keyField="herosms_api_key"
              enabledField="herosms_enabled"
              href="https://hero-sms.com/tr/api"
            />
          </div>
        </div>

        <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Fiyatlandırma</div>
          <Field label="Türkiye WhatsApp — Standart Satış Fiyatı (TL)" k="tr_wa_standard_price" mono placeholder="79.38" />
          <Field label="Türkiye WhatsApp — Premium Satış Fiyatı (TL)" k="tr_wa_premium_price" mono placeholder="99.90" />
          <Field label="Türkiye WhatsApp — Premium+ Satış Fiyatı (TL)" k="tr_wa_premium_plus_price" mono placeholder="160.00" />
          <Field label="USD → TL Döviz Kuru" k="usd_to_try" mono placeholder="34.0" />
          <Field label="Kâr Marjı Çarpanı (fiyat × X)" k="profit_multiplier" mono placeholder="1.8" />
          <div className="text-xs text-white/50 leading-relaxed">
            <b className="text-white/80">Formül:</b> Satış fiyatı = Sağlayıcı ham USD × USD/TL kuru × Kâr çarpanı.
            <br />Örnek: 0.59 USD × 34.0 × 1.8 = ₺36.11
          </div>
        </div>

        <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Havale / IBAN Bilgileri</div>
          <Field label="Alıcı Ad Soyad" k="iban_name" placeholder="Ad Soyad" />
          <Field label="IBAN" k="iban_number" mono placeholder="TR00 0000 0000 0000 0000 0000 00" />
          <Field label="Banka Adı" k="iban_bank" placeholder="Örn: Ziraat Bankası" />
        </div>

        <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Kripto Cüzdan Adresleri</div>
          <Field label="USDT (TRX)" k="crypto_usdt_trc20" mono placeholder="TR..." />
          <Field label="Bitcoin (BTC)" k="crypto_btc" mono placeholder="bc1..." />
          <Field label="Ethereum (ETH)" k="crypto_eth" mono placeholder="0x..." />
        </div>

        <div className="border border-white/10 bg-[hsl(220,40%,9%)] rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Shopier — Kredi Kartı Paketleri</div>
              <div className="text-xs text-white/50 mt-1">Aşağıdaki listede paket varsa ve toggle aktifse kullanıcıya gösterilir.</div>
            </div>
            <Toggle k="shopier_enabled" label="Shopier" />
          </div>
          <div className="text-[11px] text-white/45 leading-relaxed border-t border-white/5 pt-3">
            <b className="text-white/70">Callback URL (opsiyonel):</b> Shopier "Bildirim URL" desteklerse bu adresi ekleyin — ödeme otomatik onaylanır.
            <div className="font-mono mt-1 text-[hsl(43,74%,65%)] break-all" data-testid="shopier-callback-url">
              {(process.env.REACT_APP_BACKEND_URL || window.location.origin) + "/api/deposit/shopier/callback"}
            </div>
          </div>
          <ShopierPackagesManager />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="settings-save-btn"
          className="btn-brand disabled:opacity-50 font-bold text-sm uppercase tracking-[0.15em] px-8 py-3 rounded-sm"
        >
          {loading ? "Kaydediliyor…" : "Ayarları Kaydet"}
        </button>
      </form>

      <form onSubmit={changePwd} className="space-y-4 max-w-3xl mt-10 border border-red-500/30 bg-[hsl(0,60%,8%)] rounded-2xl p-6" data-testid="admin-change-password-form">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-400/80 mb-1">Güvenlik</div>
          <h2 className="font-display text-xl font-bold">Admin Şifresini Değiştir</h2>
          <p className="text-xs text-white/50 mt-1">Bu değişiklik anında etkindir ve kalıcıdır (env değişkenine bağlı değildir).</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Mevcut Şifre</label>
          <input
            data-testid="admin-current-password"
            type="password"
            value={pwd.current_password}
            onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
            required
            className="w-full mt-1 bg-transparent border border-white/15 focus:border-red-500/50 outline-none px-3 py-2.5 text-sm rounded-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Yeni Şifre</label>
          <input
            data-testid="admin-new-password"
            type="password"
            value={pwd.new_password}
            onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
            required
            minLength={6}
            className="w-full mt-1 bg-transparent border border-white/15 focus:border-red-500/50 outline-none px-3 py-2.5 text-sm rounded-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Yeni Şifre (Tekrar)</label>
          <input
            data-testid="admin-new-password-confirm"
            type="password"
            value={pwd.new_password_confirm}
            onChange={(e) => setPwd({ ...pwd, new_password_confirm: e.target.value })}
            required
            minLength={6}
            className="w-full mt-1 bg-transparent border border-white/15 focus:border-red-500/50 outline-none px-3 py-2.5 text-sm rounded-sm font-mono"
          />
        </div>
        <button
          type="submit"
          disabled={pwdLoading}
          data-testid="admin-change-password-btn"
          className="bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-red-300 disabled:opacity-50 font-bold text-sm uppercase tracking-[0.15em] px-6 py-2.5 rounded-sm transition-colors"
        >
          {pwdLoading ? "Değiştiriliyor…" : "Şifreyi Değiştir"}
        </button>
      </form>
    </AdminLayout>
  );
}


function ShopierPackagesManager() {
  const [pkgs, setPkgs] = useState([]);
  const [form, setForm] = useState({ label: "", amount: "", shopier_url: "" });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shopier-packages").then((r) => setPkgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!form.label.trim() || !amt || amt <= 0 || !form.shopier_url.trim()) {
      return toast.error("Ad, tutar ve Shopier link zorunlu");
    }
    if (!/^https?:\/\//.test(form.shopier_url)) {
      return toast.error("Shopier linki https:// ile başlamalı");
    }
    setSaving(true);
    try {
      const payload = { label: form.label.trim(), amount: amt, shopier_url: form.shopier_url.trim(), enabled: true };
      if (editingId) {
        await api.put(`/admin/shopier-packages/${editingId}`, payload);
        toast.success("Paket güncellendi");
      } else {
        await api.post("/admin/shopier-packages", payload);
        toast.success("Paket eklendi");
      }
      setForm({ label: "", amount: "", shopier_url: "" });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => { setEditingId(p.id); setForm({ label: p.label, amount: String(p.amount), shopier_url: p.shopier_url }); };
  const cancel = () => { setEditingId(null); setForm({ label: "", amount: "", shopier_url: "" }); };
  const toggleEnabled = async (p) => {
    try {
      await api.put(`/admin/shopier-packages/${p.id}`, { label: p.label, amount: p.amount, shopier_url: p.shopier_url, enabled: !p.enabled });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`"${p.label}" paketini silmek istediğine emin misin?`)) return;
    try {
      await api.delete(`/admin/shopier-packages/${p.id}`);
      toast.success("Paket silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-3 pt-2" data-testid="shopier-packages-manager">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Bakiye Paketleri</div>

      {pkgs.length === 0 && (
        <div className="text-xs text-white/40 italic border border-dashed border-white/10 rounded p-3">
          Henüz paket eklenmemiş. Shopier panelinden oluşturduğun sabit fiyatlı ürün linklerini aşağıya ekle.
        </div>
      )}

      <div className="space-y-2">
        {pkgs.map((p) => (
          <div key={p.id} className={`flex items-center gap-3 border ${p.enabled ? "border-white/15" : "border-white/5 opacity-50"} bg-[hsl(220,40%,7%)] rounded px-3 py-2`} data-testid={`shopier-pkg-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-bold text-sm">{p.label}</div>
                <div className="font-mono text-[hsl(43,74%,65%)] text-sm">₺{Number(p.amount).toFixed(2)}</div>
              </div>
              <a href={p.shopier_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-white/40 hover:text-[hsl(43,74%,65%)] truncate block font-mono">{p.shopier_url}</a>
            </div>
            <button type="button" onClick={() => toggleEnabled(p)} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${p.enabled ? "border-[hsl(142,71%,55%)] text-[hsl(142,71%,55%)]" : "border-white/20 text-white/50"}`} data-testid={`toggle-pkg-${p.id}`}>
              {p.enabled ? "Aktif" : "Pasif"}
            </button>
            <button type="button" onClick={() => edit(p)} className="text-[10px] font-bold uppercase tracking-wider text-white/70 hover:text-white px-2 py-1" data-testid={`edit-pkg-${p.id}`}>Düzenle</button>
            <button type="button" onClick={() => remove(p)} className="text-[10px] font-bold uppercase tracking-wider text-red-400/70 hover:text-red-400 px-2 py-1" data-testid={`delete-pkg-${p.id}`}>Sil</button>
          </div>
        ))}
      </div>

      <div className="border border-white/10 bg-[hsl(220,40%,7%)] rounded p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">
          {editingId ? "Paketi Düzenle" : "Yeni Paket Ekle"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="text"
            placeholder="Ad (Örn: ₺100 Bakiye)"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="bg-transparent border border-white/15 focus:border-[hsl(43,74%,55%)] outline-none px-3 py-2 text-sm rounded-sm"
            data-testid="shopier-pkg-label"
          />
          <input
            type="number"
            step="0.01"
            min="1"
            placeholder="Tutar (₺)"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="bg-transparent border border-white/15 focus:border-[hsl(43,74%,55%)] outline-none px-3 py-2 text-sm rounded-sm font-mono"
            data-testid="shopier-pkg-amount"
          />
          <input
            type="url"
            placeholder="https://www.shopier.com/..."
            value={form.shopier_url}
            onChange={(e) => setForm({ ...form, shopier_url: e.target.value })}
            className="bg-transparent border border-white/15 focus:border-[hsl(43,74%,55%)] outline-none px-3 py-2 text-sm rounded-sm font-mono"
            data-testid="shopier-pkg-url"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="bg-[hsl(43,74%,45%)] hover:bg-[hsl(43,74%,52%)] disabled:opacity-50 text-black font-bold text-[10px] uppercase tracking-wider px-4 py-2 rounded-sm"
            data-testid="shopier-pkg-save"
          >
            {saving ? "…" : editingId ? "Güncelle" : "+ Paket Ekle"}
          </button>
          {editingId && (
            <button type="button" onClick={cancel} className="border border-white/20 text-white/70 text-[10px] uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-white/5">
              İptal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
